/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { formatDateFull } from '../../support/DateTime';
import {
  formatChoiceWithCharge,
  parseChargeFromValue,
} from '../../support/shared/calculatePrice';
import VendorActions from '../../support/VendorActions';
import { Account } from '../../types/shared/Account';
import { FileDesc } from '../../types/shared/FileDesc';
import { Market } from '../../types/shared/Market';
import { Vendor } from '../../types/shared/Vendor';
import { confirm } from '../App/AppDialog';
import { showSuccess, showError } from '../App/AppAlert';
import SignupFormModel from '../FormBuilder/SignupFormModel';
import Preview from '../Preview';
import { BooleanInput, Button, Card, Text } from '../uiold';

import { useFetchJson } from '../../support/useFetch';
import { APP_FIELDS } from '../../support/shared/constants/Constants';
import { themeColors } from '../uiold/Constants';
import TextInput from '../uiold/TextInput';

import { useNavigation } from '../Navigation';
import { Box, Checkbox } from '../../core-ui';
import {
  ApprovalStatus,
  ChangeRequestReviewMarketsView,
} from './ChangeRequestReviewMarketsView';
import IndexedList from '../../support/shared/IndexedList';
import { MultichoiceElementDefinitionRuntype } from '../../runtypes/SignupFormDefinitionRuntype';

type Props = {
  formDef: SignupFormModel;
  vendor: Vendor;
  account: Account;
  isMarketOwner?: boolean;
  markets: Array<Market>;
  canEdit?: boolean | null | undefined;
};

type ApprovalStatusByDate = { [date: string]: ApprovalStatus };
type ApprovalDatesByMarket = { [marketId: string]: ApprovalStatusByDate };

export default function ProfileCard(props: Props) {
  let { formDef, vendor, account, isMarketOwner, markets, canEdit } = props;
  let pendingFieldChanges = vendor.pendingFieldChanges ?? [];
  let { navigate } = useNavigation();
  let fetchJson = useFetchJson();
  let [fieldApprovals, setFieldApprovals] = useState(
    () => new Map<string, boolean>(),
  );
  // This should probably be a ref if we're not using the setter
  let [marketDateApprovals] = useState<ApprovalDatesByMarket>({});

  let [previewAttachment, setPreviewAttachment] = useState<
    FileDesc | null | undefined
  >(null);
  let [reviewNote, setReviewNote] = useState('');
  let [showChangedOnly, setShowChangedOnly] = useState(true);
  let currency = account.settings && account.settings.currency;

  let handleApprovals = (value: boolean, fieldID: string) => {
    setFieldApprovals((oldFieldApprovals) => {
      const fieldApprovals = new Map(oldFieldApprovals);
      fieldApprovals.set(fieldID, value);
      return fieldApprovals;
    });
  };

  let onFileClick = (file: FileDesc, event: Event) => {
    event.preventDefault();
    setPreviewAttachment(file);
  };

  const onSubmit = async () => {
    const proceed = await confirm(
      t('Save and Send?'),
      t('Press OK to save changes. An email will be sent to this vendor.'),
    );
    if (!proceed) {
      return;
    }
    const approvedFields: Array<string> = [];
    for (const [fieldId, approved] of fieldApprovals) {
      if (approved) {
        approvedFields.push(fieldId);
      }
    }
    let response = await fetchJson(`/api/change-requests/${vendor.id}`, {
      body: {
        approvedFields,
        marketDateApprovals,
        reviewNote,
      },
    });
    if (response.success) {
      const { vendor } = response;
      VendorActions.updateVendor(vendor.id, {
        ...vendor,
        fields: new IndexedList(vendor.fields),
      });
      showSuccess(t('Saved successfully'));
      navigate('/vendor-change-requests');
    } else {
      // eslint-disable-next-line no-console
      console.log(response);
      showError(t('Error saving changes'));
    }
  };

  let items = [];
  let filteredFields = formDef.fields.toArray();

  for (let fieldDef of filteredFields) {
    let field = vendor.fields.getByID(fieldDef.id);
    let value = field == null ? null : field.value;
    let { definition, id } = fieldDef;

    if (MultichoiceElementDefinitionRuntype.guard(field)) {
      let optionsWithCharge = parseChargeFromValue(field.options);

      value = optionsWithCharge.map(({ name, charge, operator = '+' }) => {
        return formatChoiceWithCharge(name, charge, currency, operator);
      });
    }

    const pendingChange = pendingFieldChanges?.find((field) => field.id === id);

    let elementOld = fieldDef.renderReadOnlyView({
      value,
      isAdmin: true,
      definition: fieldDef.definition,
      onFileClick,
      fields: vendor.fields,
    } as any);

    let elementNew = fieldDef.renderReadOnlyView({
      value: pendingChange?.newValue,
      isAdmin: true,
      definition: fieldDef.definition,
      onFileClick,
      fields: vendor.fields,
    } as any);

    if (elementOld != null) {
      let name = typeof definition.name === 'string' ? definition.name : '';
      if (pendingChange && isMarketOwner) {
        items.push(
          <View key={`view-${id}`} style={styles.profileColumn}>
            <Text type="label" style={styles.headerText}>
              {name}
            </Text>
            {field?.id === APP_FIELDS.ID_FIELD_MARKETS ? (
              <ChangeRequestReviewMarketsView
                field={field}
                pendingChange={pendingChange}
                markets={markets}
                onApprovalChange={(marketId, date, approvalStatus) => {
                  // I'm intentionally mutating since we don't need to trigger a
                  // re-render. Not sure if this is a good idea or not.
                  const approvalsByDate =
                    marketDateApprovals[marketId] ??
                    (marketDateApprovals[marketId] = {});
                  approvalsByDate[date] = approvalStatus;
                }}
              />
            ) : (
              <>
                <Card
                  style={[
                    styles.item,
                    {
                      backgroundColor: fieldApprovals.get(id)
                        ? ''
                        : themeColors.redToast,
                    },
                  ]}
                >
                  <Text style={[styles.itemMargin, styles.red]}>
                    {t('Current: ')}
                  </Text>
                  {typeof elementOld === 'string' ? (
                    <Text>{elementOld}</Text>
                  ) : (
                    elementOld
                  )}
                </Card>
                <Card
                  style={[
                    styles.item,
                    {
                      backgroundColor: fieldApprovals.get(id)
                        ? themeColors.greenToast
                        : '',
                    },
                  ]}
                >
                  <Text style={[styles.itemMargin, styles.green]}>
                    {t('Requested Change:')}
                  </Text>
                  {typeof elementNew === 'string' ? (
                    <Text>{elementNew}</Text>
                  ) : (
                    elementNew
                  )}
                </Card>
                <View style={styles.item}>
                  <BooleanInput
                    trueParams={{ label: t('Approve'), style: styles.green }}
                    falseParams={{ label: t('Reject'), style: styles.red }}
                    value={fieldApprovals.get(id) ?? false}
                    onChange={(value) => {
                      handleApprovals(value, id);
                    }}
                  />
                </View>
              </>
            )}
          </View>,
        );
      } else {
        !showChangedOnly &&
          items.push(
            <View key={`view-${id}`} style={styles.profileColumn}>
              <Text type="label" style={styles.headerText}>
                {name}
              </Text>
              {typeof elementOld === 'string' ? (
                <Text>{elementOld}</Text>
              ) : (
                elementOld
              )}
            </View>,
          );
      }
    }
  }
  return (
    <Card>
      <Card.Header
        title={
          String(
            vendor.fields.list.find(
              (field) => field.id === APP_FIELDS.ID_FIELD_NAME,
            )?.value,
          ) ?? ''
        }
      />
      <Box>
        <Checkbox
          isChecked={showChangedOnly}
          onChange={(e) => {
            setShowChangedOnly(e.target.checked);
          }}
        >
          {t('Show only changed fields')}
        </Checkbox>
      </Box>
      <View style={styles.profileCard}>
        {items.length > 0 ? items : null}
        <View style={styles.profileColumn}>
          <Text type="label" style={styles.headerText}>
            {t('Sign Up Date')}
          </Text>
          <Text type="label">
            {formatDateFull(vendor.createdAt, false, false)}
          </Text>
        </View>
        <View style={styles.profileColumn}>
          <Text type="label" style={styles.headerText}>
            {t('Request Note')}
          </Text>
          <Text>{vendor.requestNote}</Text>
        </View>
      </View>
      {canEdit && (
        <View>
          <Text style={styles.marginVertical}>
            {t('Review Notes (visible to vendor)')}
          </Text>
          <TextInput
            type="text"
            multiline={true}
            value={reviewNote}
            onChangeText={(value) => {
              setReviewNote(value);
            }}
          />
        </View>
      )}
      {canEdit && (
        <View style={{ alignItems: 'flex-end' }}>
          <Button
            onPress={() => onSubmit()}
            title={t('Submit')}
            isPrimary
            style={styles.marginVertical}
          />
        </View>
      )}

      {previewAttachment ? (
        <Preview
          file={previewAttachment}
          disablePageScroll={true}
          onClose={() => {
            setPreviewAttachment(null);
          }}
        />
      ) : null}
    </Card>
  );
}

const styles = StyleSheet.create({
  profileColumn: {
    marginVertical: 20,
    marginHorizontal: 30,
  },
  profileCard: {
    flexDirection: 'column',
    flexWrap: 'wrap',
  },
  item: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 4,
  },
  green: { color: themeColors.greenButtonHovered },
  red: { color: themeColors.red },
  itemMargin: { marginRight: 20 },
  headerText: {
    marginTop: 10,
    marginBottom: 5,
    color: themeColors.darkGrey,
  },
  marginVertical: {
    marginVertical: 30,
  },
});
